// https://mantinehub.com
import {
  ActionIcon,
  Alert,
  Anchor,
  AppShell,
  Avatar,
  Badge,
  Blockquote,
  Button,
  Card,
  Checkbox,
  Chip,
  Container,
  createTheme,
  Dialog,
  Indicator,
  type MantineColorsTuple,
  type MantineThemeOverride,
  Mark,
  NavLink,
  Pagination,
  Paper,
  Radio,
  rem,
  SegmentedControl,
  Select,
  Stepper,
  Switch,
  ThemeIcon,
  Timeline,
  Title,
  Tooltip,
} from '@mantine/core'
import {
  amberColors,
  blueColors,
  cyanColors,
  cyberBlueColors,
  deepIndigoColors,
  electricCyanColors,
  emeraldColors,
  fuchsiaColors,
  grapeColors,
  grayColors,
  greenColors,
  hotMagentaColors,
  indigoColors,
  limeColors,
  neutralColors,
  orangeColors,
  pinkColors,
  purpleColors,
  redColors,
  roseColors,
  skyColors,
  slateColors,
  stoneColors,
  tealColors,
  violetColors,
  yellowColors,
  zincColors,
} from './colors'

const CONTAINER_SIZES: Record<string, string> = {
  xxs: rem('200px'),
  xs: rem('300px'),
  sm: rem('400px'),
  md: rem('500px'),
  lg: rem('600px'),
  xl: rem('1400px'),
  xxl: rem('1600px'),
}

// ---------------------------------------------------------------------------
// Theme system
// ---------------------------------------------------------------------------
// To add a new theme, add one entry to THEME_DEFINITIONS below.
//
// primaryKey   – key into PRIMARY_COLOR_MAP (the accent color palette)
// secondaryKey – key into SECONDARY_COLOR_MAP (backgrounds, borders, surfaces)
//                Follows the same pairing used by mantinehub.com/shadcn-themes:
//                  warm colors (rose/orange/amber/green) → 'stone'
//                  cool blues  (sky/teal/cyan)           → 'slate'
//                  purples     (violet/indigo/purple)    → 'gray'
//                  neutrals    (zinc/slate/etc.)         → same as primary
// contrastColor – text color rendered on top of filled primary buttons
// ---------------------------------------------------------------------------

const PRIMARY_COLOR_MAP = {
  electricCyan: electricCyanColors,
  grape: grapeColors,
  pink: pinkColors,
  red: redColors,
  violet: violetColors,
  indigo: indigoColors,
  rose: roseColors,
  orange: orangeColors,
  emerald: emeraldColors,
  teal: tealColors,
  cyan: cyanColors,
  sky: skyColors,
  amber: amberColors,
  gray: grayColors,
  zinc: zincColors,
}

const SECONDARY_COLOR_MAP = {
  zinc: zincColors,
  slate: slateColors,
  stone: stoneColors,
  gray: grayColors,
  neutral: neutralColors,
}

export type PrimaryColorKey = keyof typeof PRIMARY_COLOR_MAP
export type SecondaryColorKey = keyof typeof SECONDARY_COLOR_MAP

export interface ThemeDefinition {
  id: string
  label: string
  /** Hex shown in the swatch picker */
  swatch: string
  /** Primary color palette — drives buttons, links, active UI elements */
  primaryKey: PrimaryColorKey
  /** Neutral palette — drives all backgrounds, borders, surfaces */
  secondaryKey: SecondaryColorKey
  /** Text color rendered on filled primary-color buttons */
  contrastColor: string
  /** "From" accent — gradient-title start + active icon color (shuffle, hearts).
   *  Any CSS color value. Defaults to var(--mantine-color-primary-3). */
  colorFrom?: string
  /** "To" accent — gradient-title end + play button fill.
   *  Any CSS color value. Defaults to var(--mantine-color-primary-7). */
  colorTo?: string
  /** Waveform canvas gradient: [outer, mid, inner] as hex strings.
   *  Outer renders at the top/bottom edges, inner at the amplitude center.
   *  Defaults to primary palette shades [3], [5], [7]. */
  waveformColors?: [string, string, string]
  /** Logo gradient colors: [a, b, c, d] where a=outer/bright, b=mid, c=deep, d=arrow tip accent.
   *  Defaults to the Hypersonic palette. */
  logoColors?: [string, string, string, string]
  /** Dark-mode page background color. Defaults to secondary-9 (neutral dark).
   *  Use a very dark tinted hex to give the background a thematic hue. */
  darkBackground?: string
}

const THEME_DEFINITIONS: ThemeDefinition[] = [
  {
    id: 'hypersonic',
    label: 'Hypersonic',
    swatch: '#00F2FF',
    primaryKey: 'electricCyan',
    secondaryKey: 'slate',
    contrastColor: '#0f172a',
    colorFrom: 'var(--mantine-color-hotMagenta-5)',
    colorTo: 'var(--mantine-color-deepIndigo-6)',
    waveformColors: ['#00FFFF', '#46BEFF', '#8C82FF'],
    logoColors: ['#00F2FF', '#3D52FF', '#6A00FF', '#FF2E95'],
    darkBackground: '#060d14',
  },
  {
    id: 'cyberpunk',
    label: 'Cyberpunk',
    swatch: '#fdf001',
    primaryKey: 'cyan',
    secondaryKey: 'zinc',
    contrastColor: '#000000',
    colorFrom: '#00f3ff',
    colorTo: '#fdf001',
    waveformColors: ['#0a0a0a', '#00f3ff', '#fdf001'],
    logoColors: ['#fdf001', '#00c0ff', '#0040c0', '#00f3ff'],
    darkBackground: '#09090a',
  },
  // ember: heat leaving a system — coals in their final hour, patient and dim and still going
  {
    id: 'rose',
    label: 'Ember',
    swatch: '#6e1a08',
    primaryKey: 'rose',
    secondaryKey: 'stone',
    contrastColor: '#ffffff',
    colorFrom: '#9e2410',
    colorTo: '#c03020',
    waveformColors: ['#140604', '#4a1208', '#9a2010'],
    logoColors: ['#FF6030', '#C02010', '#600804', '#FF2040'],
    darkBackground: '#110604',
  },
  // eclipse: corona only bright because everything around it went completely dark
  {
    id: 'orange',
    label: 'Eclipse',
    swatch: '#08001e',
    primaryKey: 'indigo',
    secondaryKey: 'zinc',
    contrastColor: '#ffffff',
    colorFrom: '#f0e8ff',
    colorTo: '#7040e0',
    waveformColors: ['#020006', '#07001c', '#ede0ff'],
    logoColors: ['#d0c0ff', '#7040f0', '#200060', '#a020ff'],
    darkBackground: '#05000f',
  },
  // aurora: bioluminescence asserting itself against the frozen and lifeless — quiet, alien, unsettling
  {
    id: 'emerald',
    label: 'Aurora',
    swatch: '#009980',
    primaryKey: 'teal',
    secondaryKey: 'slate',
    contrastColor: '#000d0a',
    colorFrom: '#00c8a0',
    colorTo: '#007a60',
    waveformColors: ['#01080e', '#003830', '#009878'],
    logoColors: ['#00FFcc', '#00C090', '#005040', '#00c8a0'],
    darkBackground: '#020d0b',
  },
  // twilight: bruised violet overhead collapsing inward, last amber dying at the horizon
  {
    id: 'sky',
    label: 'Twilight',
    swatch: '#3a1a80',
    primaryKey: 'violet',
    secondaryKey: 'stone',
    contrastColor: '#ffffff',
    colorFrom: '#5028a8',
    colorTo: '#9a5820',
    waveformColors: ['#0a0520', '#401080', '#9a5820'],
    logoColors: ['#c090ff', '#6030c0', '#300880', '#ff8040'],
    darkBackground: '#080709',
  },
  // forge: sparks aren't decorative — proof of force applied to resistant material
  {
    id: 'forge',
    label: 'Forge',
    swatch: '#A52A2A',
    primaryKey: 'orange',
    secondaryKey: 'zinc',
    contrastColor: '#ffffff',
    colorFrom: '#FF4500',
    colorTo: '#8B4513',
    waveformColors: ['#1C1C1C', '#FF4500', '#FFD700'],
    logoColors: ['#FFD030', '#FF5020', '#801008', '#FF4500'],
    darkBackground: '#110c09',
  },
  {
    id: 'grove',
    label: 'Grove',
    swatch: '#2F4F2F',
    primaryKey: 'emerald',
    secondaryKey: 'slate',
    contrastColor: '#ffffff',
    colorFrom: '#228B22',
    colorTo: '#8FBC8F',
    waveformColors: ['#1A2421', '#228B22', '#F0FFF0'],
    logoColors: ['#80FF80', '#30A030', '#104010', '#8FBC8F'],
    darkBackground: '#050a12',
  },
  // ash: visual silence after a long noise — no heat, no urgency, just what's left
  {
    id: 'zinc',
    label: 'Ash',
    swatch: '#786860',
    primaryKey: 'gray',
    secondaryKey: 'neutral',
    contrastColor: '#ffffff',
    colorFrom: '#685e58',
    colorTo: '#b0a898',
    waveformColors: ['#181410', '#484038', '#887870'],
    logoColors: ['#C8C0B8', '#888078', '#403830', '#b0a898'],
    darkBackground: '#0a0a0a',
  },
]

export interface AppTheme {
  id: string
  label: string
  swatch: string
  colors: MantineColorsTuple
  secondary: MantineColorsTuple
  contrastColor: string
  colorFrom?: string
  colorTo?: string
  waveformColors?: [string, string, string]
  logoColors?: [string, string, string, string]
  darkBackground?: string
}

export const APP_THEMES: AppTheme[] = THEME_DEFINITIONS.map((def) => ({
  id: def.id,
  label: def.label,
  swatch: def.swatch,
  colors: PRIMARY_COLOR_MAP[def.primaryKey] ?? electricCyanColors,
  secondary: SECONDARY_COLOR_MAP[def.secondaryKey],
  contrastColor: def.contrastColor,
  colorFrom: def.colorFrom,
  colorTo: def.colorTo,
  waveformColors: def.waveformColors,
  logoColors: def.logoColors,
  darkBackground: def.darkBackground,
}))

export const baseTheme: MantineThemeOverride = createTheme({
  colors: {
    slate: slateColors,
    gray: grayColors,
    zinc: zincColors,
    neutral: neutralColors,
    stone: stoneColors,

    red: redColors,
    rose: roseColors,
    orange: orangeColors,
    amber: amberColors,
    yellow: yellowColors,

    lime: limeColors,
    green: greenColors,
    emerald: emeraldColors,

    teal: tealColors,
    cyan: cyanColors,
    sky: skyColors,
    blue: blueColors,

    indigo: indigoColors,
    violet: violetColors,
    grape: grapeColors,
    purple: purpleColors,
    fuchsia: fuchsiaColors,
    pink: pinkColors,

    electricCyan: electricCyanColors,
    cyberBlue: cyberBlueColors,
    hotMagenta: hotMagentaColors,
    deepIndigo: deepIndigoColors,

    primary: electricCyanColors,
    secondary: slateColors,
    dark: slateColors,

    error: redColors as MantineColorsTuple,
    success: greenColors,
    info: blueColors,
    warning: amberColors,
  },
  focusRing: 'never',
  scale: 1,
  primaryColor: 'primary',
  primaryShade: { light: 5, dark: 4 },
  autoContrast: true,
  luminanceThreshold: 0.3,
  fontFamily: "'JetBrains Mono', monospace",
  fontFamilyMonospace: "'JetBrains Mono', monospace",
  radius: {
    xs: rem('6px'),
    sm: rem('8px'),
    md: rem('12px'),
    lg: rem('16px'),
    xl: rem('24px'),
  },
  defaultRadius: 'sm',
  spacing: {
    '4xs': rem('2px'),
    '3xs': rem('4px'),
    '2xs': rem('8px'),
    xs: rem('10px'),
    sm: rem('12px'),
    md: rem('16px'),
    lg: rem('20px'),
    xl: rem('24px'),
    '2xl': rem('28px'),
    '3xl': rem('32px'),
    '4xl': rem('40px'),
  },
  fontSizes: {
    xs: rem('12px'),
    sm: rem('14px'),
    md: rem('16px'),
    lg: rem('18px'),
    xl: rem('20px'),
    '2xl': rem('24px'),
    '3xl': rem('30px'),
    '4xl': rem('36px'),
    '5xl': rem('48px'),
  },
  lineHeights: {
    xs: rem('18px'),
    sm: rem('20px'),
    md: rem('24px'),
    lg: rem('28px'),
  },

  headings: {
    fontFamily: "'Orbitron', sans-serif",
    sizes: {
      h1: {
        fontSize: rem('36px'),
        lineHeight: rem('44px'),
        fontWeight: '700',
      },
      h2: {
        fontSize: rem('30px'),
        lineHeight: rem('38px'),
        fontWeight: '700',
      },
      h3: {
        fontSize: rem('24px'),
        lineHeight: rem('32px'),
        fontWeight: '700',
      },
      h4: {
        fontSize: rem('20px'),
        lineHeight: rem('30px'),
        fontWeight: '600',
      },
    },
  },
  shadows: {
    xs: '0 1px 2px rgba(0, 0, 0, 0.05)',
    sm: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
    md: '0 4px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px rgba(0, 0, 0, 0.1), 0 4px 6px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px rgba(0, 0, 0, 0.1), 0 10px 10px rgba(0, 0, 0, 0.04)',
    xxl: '0 25px 50px rgba(0, 0, 0, 0.25)',
  },

  cursorType: 'pointer',
  other: {
    style: 'shadcn',
  },
  // NOTE: `primary` is overridden at runtime by buildTheme() — this is the default
  components: {
    AppShell: AppShell.extend({
      styles: {
        root: {
          '--app-shell-border-color': 'var(--mantine-color-default-border)',
        },
      },
    }),
    Container: Container.extend({
      vars: (_, { size, fluid }) => ({
        root: {
          '--container-size': fluid
            ? '100%'
            : size !== undefined && size in CONTAINER_SIZES
              ? CONTAINER_SIZES[size]
              : rem(size),
        },
      }),
    }),
    Checkbox: Checkbox.extend({
      vars: (theme, props) => {
        const colorKey =
          props.color && Object.keys(theme.colors).includes(props.color) ? props.color : undefined
        return {
          root: {
            '--checkbox-color': colorKey
              ? `var(--mantine-color-${colorKey}-filled)`
              : 'var(--mantine-primary-color-filled)',

            '--checkbox-icon-color': colorKey
              ? `var(--mantine-color-${colorKey}-contrast)`
              : 'var(--mantine-primary-color-contrast)',
          },
        }
      },
    }),
    Chip: Chip.extend({
      vars: (theme, props) => {
        const colorKey =
          props.color && Object.keys(theme.colors).includes(props.color) ? props.color : undefined
        const variant = props.variant ?? 'filled'
        return {
          root: {
            '--chip-bg':
              variant !== 'light'
                ? colorKey
                  ? `var(--mantine-color-${colorKey}-filled)`
                  : 'var(--mantine-primary-color-filled)'
                : undefined,
            '--chip-color':
              variant === 'filled'
                ? colorKey
                  ? `var(--mantine-color-${colorKey}-contrast)`
                  : 'var(--mantine-primary-color-contrast)'
                : undefined,
          },
        }
      },
    }),
    Radio: Radio.extend({
      vars: (theme, props) => ({
        root: {
          '--radio-color': props.color
            ? Object.keys(theme.colors).includes(props.color)
              ? `var(--mantine-color-${props.color}-filled)`
              : props.color
            : 'var(--mantine-primary-color-filled)',

          '--radio-icon-color': props.color
            ? Object.keys(theme.colors).includes(props.color)
              ? `var(--mantine-color-${props.color}-contrast)`
              : props.color
            : 'var(--mantine-primary-color-contrast)',
        },
      }),
    }),
    SegmentedControl: SegmentedControl.extend({
      vars: (theme, props) => ({
        root: {
          '--sc-color': props.color
            ? Object.keys(theme.colors).includes(props.color)
              ? ['zinc', 'slate', 'gray', 'neutral', 'stone'].includes(props.color)
                ? 'var(--mantine-color-body)'
                : `var(--mantine-color-${props.color}-filled)`
              : props.color
            : 'var(--mantine-color-default)',
        },
      }),
    }),
    Switch: Switch.extend({
      styles: () => ({
        thumb: {
          backgroundColor: 'var(--mantine-color-default)',
          borderColor: 'var(--mantine-color-default-border)',
        },
        track: {
          borderColor: 'var(--mantine-color-default-border)',
        },
      }),
    }),
    Select: Select.extend({
      defaultProps: {
        checkIconPosition: 'right',
      },
    }),
    ActionIcon: ActionIcon.extend({
      vars: (theme, props) => {
        const colorKey =
          props.color && Object.keys(theme.colors).includes(props.color) ? props.color : undefined
        const isNeutralColor =
          colorKey && ['zinc', 'slate', 'gray', 'neutral', 'stone'].includes(colorKey)
        const isNeutralPrimaryColor =
          !colorKey && ['zinc', 'slate', 'gray', 'neutral', 'stone'].includes(theme.primaryColor)
        const variant = props.variant ?? 'filled'

        return {
          root: {
            '--ai-color': (() => {
              if (variant === 'filled') {
                if (colorKey) {
                  return `var(--mantine-color-${colorKey}-contrast)`
                }
                return 'var(--mantine-primary-color-contrast)'
              }
              if (variant === 'white') {
                if (isNeutralColor || isNeutralPrimaryColor) {
                  return 'var(--mantine-color-black)'
                }
                return undefined
              }
              return undefined
            })(),
          },
        }
      },
    }),
    Button: Button.extend({
      vars: (theme, props) => {
        const colorKey =
          props.color && Object.keys(theme.colors).includes(props.color) ? props.color : undefined
        const isNeutralColor =
          colorKey && ['zinc', 'slate', 'gray', 'neutral', 'stone'].includes(colorKey)
        const isNeutralPrimaryColor =
          !colorKey && ['zinc', 'slate', 'gray', 'neutral', 'stone'].includes(theme.primaryColor)
        const variant = props.variant ?? 'filled'
        return {
          root: {
            '--button-color': (() => {
              if (variant === 'filled') {
                if (colorKey) {
                  return `var(--mantine-color-${colorKey}-contrast)`
                }
                return 'var(--mantine-primary-color-contrast)'
              }
              if (variant === 'white') {
                if (isNeutralColor || isNeutralPrimaryColor) {
                  return 'var(--mantine-color-black)'
                }
                return undefined
              }
              return undefined
            })(),
          },
        }
      },
    }),
    Anchor: Anchor.extend({
      defaultProps: {
        underline: 'always',
      },
    }),
    NavLink: NavLink.extend({
      vars: (theme, props) => {
        const colorKey =
          props.color && Object.keys(theme.colors).includes(props.color) ? props.color : undefined
        const variant = props.variant ?? 'light'
        return {
          root: {
            '--nl-color':
              variant === 'filled'
                ? colorKey
                  ? `var(--mantine-color-${colorKey}-contrast)`
                  : 'var(--mantine-primary-color-contrast)'
                : undefined,
          },
          children: {},
        }
      },
    }),
    Pagination: Pagination.extend({
      vars: (theme, props) => {
        const colorKey =
          props.color && Object.keys(theme.colors).includes(props.color) ? props.color : undefined
        return {
          root: {
            '--pagination-active-color': colorKey
              ? `var(--mantine-color-${colorKey}-contrast)`
              : 'var(--mantine-primary-color-contrast)',
          },
        }
      },
    }),
    Stepper: Stepper.extend({
      vars: (theme, props) => {
        const colorKey =
          props.color && Object.keys(theme.colors).includes(props.color) ? props.color : undefined
        return {
          root: {
            '--stepper-icon-color': colorKey
              ? `var(--mantine-color-${colorKey}-contrast)`
              : 'var(--mantine-primary-color-contrast)',
          },
        }
      },
    }),
    Alert: Alert.extend({
      vars: (theme, props) => {
        const colorKey =
          props.color && Object.keys(theme.colors).includes(props.color) ? props.color : undefined
        const isNeutralColor =
          colorKey && ['zinc', 'slate', 'gray', 'neutral', 'stone'].includes(colorKey)
        const isNeutralPrimaryColor =
          !colorKey && ['zinc', 'slate', 'gray', 'neutral', 'stone'].includes(theme.primaryColor)
        const variant = props.variant ?? 'light'
        return {
          root: {
            '--alert-color':
              variant === 'filled'
                ? colorKey
                  ? `var(--mantine-color-${colorKey}-contrast)`
                  : 'var(--mantine-primary-color-contrast)'
                : variant === 'white'
                  ? isNeutralColor || isNeutralPrimaryColor
                    ? `var(--mantine-color-black)`
                    : undefined
                  : undefined,
          },
        }
      },
    }),
    Dialog: Dialog.extend({
      defaultProps: {
        withBorder: true,
      },
    }),
    Tooltip: Tooltip.extend({
      vars: () => ({
        tooltip: {
          '--tooltip-bg': 'var(--mantine-color-primary-color-filled)',
          '--tooltip-color': 'var(--mantine-color-primary-color-contrast)',
        },
      }),
    }),
    Avatar: Avatar.extend({
      vars: (theme, props) => {
        const colorKey =
          props.color && Object.keys(theme.colors).includes(props.color) ? props.color : undefined
        const isNeutralColor =
          colorKey && ['zinc', 'slate', 'gray', 'neutral', 'stone'].includes(colorKey)
        const isNeutralPrimaryColor =
          !colorKey && ['zinc', 'slate', 'gray', 'neutral', 'stone'].includes(theme.primaryColor)
        const variant = props.variant ?? 'light'
        return {
          root: {
            '--avatar-bg':
              variant === 'filled'
                ? colorKey
                  ? `var(--mantine-color-${colorKey}-filled)`
                  : 'var(--mantine-primary-color-filled)'
                : variant === 'light'
                  ? colorKey
                    ? `var(--mantine-color-${colorKey}-light)`
                    : 'var(--mantine-primary-color-light)'
                  : undefined,

            '--avatar-color':
              variant === 'filled'
                ? colorKey
                  ? `var(--mantine-color-${colorKey}-contrast)`
                  : 'var(--mantine-primary-color-contrast)'
                : variant === 'light'
                  ? colorKey
                    ? `var(--mantine-color-${colorKey}-light-color)`
                    : 'var(--mantine-primary-color-light-color)'
                  : variant === 'white'
                    ? isNeutralColor || isNeutralPrimaryColor
                      ? `var(--mantine-color-black)`
                      : colorKey
                        ? `var(--mantine-color-${colorKey}-outline)`
                        : 'var(--mantine-primary-color-filled)'
                    : variant === 'outline' || variant === 'transparent'
                      ? colorKey
                        ? `var(--mantine-color-${colorKey}-outline)`
                        : 'var(--mantine-primary-color-filled)'
                      : undefined,

            '--avatar-bd':
              variant === 'outline'
                ? colorKey
                  ? `1px solid var(--mantine-color-${colorKey}-outline)`
                  : '1px solid var(--mantine-primary-color-filled)'
                : undefined,
          },
        }
      },
    }),
    Badge: Badge.extend({
      vars: (theme, props) => {
        const colorKey =
          props.color && Object.keys(theme.colors).includes(props.color) ? props.color : undefined
        const isNeutralColor =
          colorKey && ['zinc', 'slate', 'gray', 'neutral', 'stone'].includes(colorKey)
        const isNeutralPrimaryColor =
          !colorKey && ['zinc', 'slate', 'gray', 'neutral', 'stone'].includes(theme.primaryColor)
        const variant = props.variant ?? 'filled'
        return {
          root: {
            '--badge-bg':
              variant === 'filled' && colorKey
                ? `var(--mantine-color-${colorKey}-filled)`
                : undefined,
            '--badge-color':
              variant === 'filled'
                ? colorKey
                  ? `var(--mantine-color-${colorKey}-contrast)`
                  : 'var(--mantine-primary-color-contrast)'
                : variant === 'white'
                  ? isNeutralColor || isNeutralPrimaryColor
                    ? `var(--mantine-color-black)`
                    : undefined
                  : undefined,
          },
        }
      },
    }),
    Card: Card.extend({
      defaultProps: {
        p: 'xl',
        shadow: '0',
        withBorder: true,
      },
      styles: (theme) => {
        return {
          root: {
            backgroundColor:
              theme.primaryColor === 'rose' || theme.primaryColor === 'green'
                ? 'var(--mantine-color-secondary-filled)'
                : undefined,
          },
        }
      },
    }),
    Indicator: Indicator.extend({
      vars: (theme, props) => {
        const colorKey =
          props.color && Object.keys(theme.colors).includes(props.color) ? props.color : undefined
        return {
          root: {
            '--indicator-text-color': colorKey
              ? `var(--mantine-color-${colorKey}-contrast)`
              : 'var(--mantine-primary-color-contrast)',
          },
        }
      },
    }),
    ThemeIcon: ThemeIcon.extend({
      vars: (theme, props) => {
        const colorKey =
          props.color && Object.keys(theme.colors).includes(props.color) ? props.color : undefined
        const isNeutralColor =
          colorKey && ['zinc', 'slate', 'gray', 'neutral', 'stone'].includes(colorKey)
        const isNeutralPrimaryColor =
          !colorKey && ['zinc', 'slate', 'gray', 'neutral', 'stone'].includes(theme.primaryColor)

        const variant = props.variant ?? 'filled'
        return {
          root: {
            '--ti-color':
              variant === 'filled'
                ? colorKey
                  ? `var(--mantine-color-${colorKey}-contrast)`
                  : 'var(--mantine-primary-color-contrast)'
                : variant === 'white'
                  ? isNeutralColor || isNeutralPrimaryColor
                    ? `var(--mantine-color-black)`
                    : undefined
                  : undefined,
          },
        }
      },
    }),
    Timeline: Timeline.extend({
      vars: (theme, props) => {
        const colorKey =
          props.color && Object.keys(theme.colors).includes(props.color) ? props.color : undefined
        return {
          root: {
            '--tl-icon-color': colorKey
              ? `var(--mantine-color-${colorKey}-contrast)`
              : 'var(--mantine-primary-color-contrast)',
          },
        }
      },
    }),
    Blockquote: Blockquote.extend({
      vars: (theme, props) => {
        const colorKey =
          props.color && Object.keys(theme.colors).includes(props.color) ? props.color : undefined
        return {
          root: {
            '--bq-bg-dark': colorKey
              ? `var(--mantine-color-${colorKey}-light)`
              : 'var(--mantine-primary-color-light)',
            '--bq-bg-light': colorKey
              ? `var(--mantine-color-${colorKey}-light)`
              : 'var(--mantine-primary-color-light)',
          },
        }
      },
    }),
    Mark: Mark.extend({
      vars: (theme, props) => {
        const colorKey =
          props.color && Object.keys(theme.colors).includes(props.color) ? props.color : 'yellow'
        const isNeutralColor =
          colorKey && ['zinc', 'slate', 'gray', 'neutral', 'stone'].includes(colorKey)
        return {
          root: {
            '--mark-bg-light': `var(--mantine-color-${colorKey}-${isNeutralColor ? '3' : 'filled-hover'})`,
            '--mark-bg-dark': `var(--mantine-color-${colorKey}-filled)`,
          },
        }
      },
    }),
    Title: Title.extend({
      classNames: {
        root: 'gradient-title',
      },
    }),
    Paper: Paper.extend({
      defaultProps: {
        shadow: '0',
      },
    }),
  },
})

export const buildTheme = (themeId: string): MantineThemeOverride => {
  const appTheme = APP_THEMES.find((t) => t.id === themeId) ?? APP_THEMES[0]
  return {
    ...baseTheme,
    colors: {
      ...(baseTheme.colors as Record<string, MantineColorsTuple>),
      primary: appTheme.colors,
      secondary: appTheme.secondary,
      dark: appTheme.secondary,
    },
    other: {
      ...baseTheme.other,
      primaryContrast: appTheme.contrastColor,
      gradientFrom: appTheme.colorFrom,
      accentSecondary: appTheme.colorFrom,
      gradientTo: appTheme.colorTo,
      accentFill: appTheme.colorTo,
      waveformColors: appTheme.waveformColors,
      darkBackground: appTheme.darkBackground,
    },
  }
}

// kept for backwards-compat with any direct imports
export const theme = buildTheme('hypersonic')
